import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import type { MockTool, MockToolRepair } from '@/mocks/tools';
import { toolService } from '@/services/toolService';
import SearchableSelect from '@/components/base/SearchableSelect';

const CATEGORY_LABELS: Record<MockTool['category'], string> = {
  DIE: 'Die', FIXTURE: 'Fixture', JIGS: 'Jigs', CUTTING_TOOL: 'Cutting Tool', MOULD: 'Mould', GAUGE: 'Gauge',
};

const STATUS_BADGES: Record<string, { color: string; label: string }> = {
  ACTIVE: { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Active' },
  UNDER_REPAIR: { color: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Under Repair' },
  RETIRED: { color: 'bg-slate-100 text-slate-600 border-slate-200', label: 'Retired' },
  SCRAPPED: { color: 'bg-rose-50 text-rose-700 border-rose-200', label: 'Scrapped' },
};

export default function ToolDetailPage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const navigate = useNavigate();

  const [tool, setTool] = useState<MockTool | null>(null);
  const [repairs, setRepairs] = useState<MockToolRepair[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const [showShotModal, setShowShotModal] = useState(false);
  const [showRepairModal, setShowRepairModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);

  const [shotQty, setShotQty] = useState('');
  const [shotReason, setShotReason] = useState('');

  const [repairForm, setRepairForm] = useState({
    repairType: 'MAINTENANCE' as MockToolRepair['repairType'],
    repairBy: '',
    repairCost: '',
    expectedReturn: '',
    description: '',
  });

  const [returnForm, setReturnForm] = useState({
    returnDate: new Date().toISOString().split('T')[0],
    actualCost: '',
    condition: 'Good' as 'Good' | 'Fair' | 'Poor',
    resetShots: false,
    notes: '',
  });

  const loadData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await toolService.getToolById(id);
      if (res) {
        setTool(res.tool);
        setRepairs(res.repairs || []);
      } else {
        setTool(null);
        setRepairs([]);
      }
    } catch (err) {
      console.error('Error fetching tool detail:', err);
      toast.error('Failed to load tool details');
      setTool(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const lifePct = tool
    ? tool.maxShotCount > 0
      ? (tool.currentShotCount / tool.maxShotCount) * 100
      : tool.maxLifeHours && tool.maxLifeHours > 0
        ? (tool.currentLifeHours / tool.maxLifeHours) * 100
        : 0
    : 0;

  if (loading) {
    return (
      <AppLayout>
        <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center w-full">
          <div className="flex items-center gap-3 text-slate-600 text-sm font-medium">
            <i className="ri-loader-4-line animate-spin text-2xl text-blue-600" />
            <span>Loading tool details...</span>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!tool) {
    return (
      <AppLayout>
        <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center w-full">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-slate-100 border border-slate-200 mb-4">
              <i className="ri-error-warning-line text-2xl text-slate-400" />
            </div>
            <h2 className="text-lg font-bold text-[#1e293b]">Tool not found</h2>
            <button onClick={() => navigate('/manufacturing/tools')} className="mt-3 text-sm text-[#2563eb] font-semibold hover:underline cursor-pointer">
              ← Back to Tools
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  const formatCurrency = (v: number) => '\u20B9' + v.toLocaleString('en-IN');

  const handleUpdateShots = async () => {
    const qty = Number(shotQty);
    if (!qty || qty <= 0) return;
    try {
      await toolService.updateShots(tool.id, qty, shotReason);
      setShotQty('');
      setShotReason('');
      setShowShotModal(false);
      toast.success(`${qty.toLocaleString()} shots added to ${tool.toolNumber}`);
      loadData();
    } catch (err) {
      console.error('Error updating shots:', err);
      toast.error('Failed to update shot count');
    }
  };

  const handleSendRepair = async () => {
    if (!repairForm.repairBy.trim()) {
      toast.error('Repair By (Vendor/Technician) is required');
      return;
    }
    try {
      await toolService.sendForRepair(tool.id, {
        repairType: repairForm.repairType,
        repairBy: repairForm.repairBy.trim(),
        repairCost: Number(repairForm.repairCost) || 0,
        expectedReturn: repairForm.expectedReturn || undefined,
        description: repairForm.description,
      });
      setShowRepairModal(false);
      setRepairForm({ repairType: 'MAINTENANCE', repairBy: '', repairCost: '', expectedReturn: '', description: '' });
      toast.success(`${tool.toolNumber} sent for repair`);
      loadData();
    } catch (err) {
      console.error('Error sending tool for repair:', err);
      toast.error('Failed to send tool for repair');
    }
  };

  const handleReturnRepair = async () => {
    try {
      await toolService.returnFromRepair(tool.id, {
        returnDate: returnForm.returnDate,
        actualCost: Number(returnForm.actualCost) || 0,
        condition: returnForm.condition,
        resetShots: returnForm.resetShots,
        notes: returnForm.notes,
      });
      setShowReturnModal(false);
      toast.success(`${tool.toolNumber} returned from repair`);
      loadData();
    } catch (err) {
      console.error('Error returning tool from repair:', err);
      toast.error('Failed to return tool from repair');
    }
  };

  const barColor = lifePct < 60 ? 'stroke-emerald-500' : lifePct < 80 ? 'stroke-amber-500' : 'stroke-rose-500';
  const circumference = 2 * Math.PI * 54;
  const strokeDash = (lifePct / 100) * circumference;

  return (
    <AppLayout>
      <div className="min-h-screen bg-[#f8fafc] py-6 px-4 md:px-6 w-full">
        <div className="w-full">
          {/* Header & Back Button */}
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => navigate('/manufacturing/tools')}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 cursor-pointer shadow-sm transition-colors"
              title="Back to Tools"
            >
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-[#1e293b]">{tool.toolNumber}</h1>
                <button
                  onClick={() => { navigator.clipboard.writeText(tool.toolNumber); toast.success('Copied!'); }}
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer transition-colors"
                  title="Copy Tool Number"
                >
                  <i className="ri-file-copy-line text-sm" />
                </button>
                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[11px] font-bold border ${STATUS_BADGES[tool.status]?.color || 'bg-slate-100'}`}>
                  {tool.status === 'UNDER_REPAIR' && <i className="ri-tools-line text-[10px]" />}
                  {STATUS_BADGES[tool.status]?.label || tool.status}
                </span>
              </div>
              <p className="text-sm text-[#64748b] mt-0.5 font-medium">{tool.name}</p>
            </div>

            {/* Header Right Action Cluster */}
            <div className="flex items-center gap-2.5 ml-auto flex-wrap">
              {tool.status === 'ACTIVE' && (
                <>
                  <button
                    onClick={() => setShowShotModal(true)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-sm font-semibold border border-indigo-200 transition-colors cursor-pointer whitespace-nowrap shadow-xs"
                  >
                    <i className="ri-add-line text-base" /> Update Shots
                  </button>
                  <button
                    onClick={() => setShowRepairModal(true)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 text-sm font-semibold border border-amber-200 transition-colors cursor-pointer whitespace-nowrap shadow-xs"
                  >
                    <i className="ri-tools-line text-base" /> Send for Repair
                  </button>
                </>
              )}
              {tool.status === 'UNDER_REPAIR' && (
                <button
                  onClick={() => setShowReturnModal(true)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-sm font-semibold border border-emerald-200 transition-colors cursor-pointer whitespace-nowrap shadow-xs"
                >
                  <i className="ri-logout-box-r-line text-base" /> Return from Repair
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
            {/* Life Status Panel */}
            <div className="lg:col-span-1">
              <div className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col items-center shadow-sm">
                <div className="relative w-40 h-40">
                  <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
                    <circle cx="60" cy="60" r="54" fill="none" stroke="#e2e8f0" strokeWidth="10" />
                    <circle
                      cx="60" cy="60" r="54"
                      fill="none"
                      strokeWidth="10"
                      strokeLinecap="round"
                      className={barColor}
                      strokeDasharray={circumference}
                      strokeDashoffset={circumference - strokeDash}
                      style={{ transition: 'stroke-dashoffset 0.5s ease' }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`text-2xl font-bold ${lifePct > 80 ? 'text-rose-600' : lifePct > 60 ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {lifePct.toFixed(1)}%
                    </span>
                    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Life Used</span>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <div className="text-sm font-bold text-[#1e293b]">
                    {tool.maxLifeHours && tool.maxLifeHours > 0
                      ? `${(tool.maxLifeHours - tool.currentLifeHours).toFixed(0)} hours`
                      : `${(tool.remainingShots || 0).toLocaleString()} shots`} remaining
                  </div>
                  {tool.status === 'ACTIVE' && lifePct >= tool.alertThresholdPercent && (
                    <div className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-700 text-xs font-bold">
                      <i className="ri-alert-line" /> Schedule replacement soon
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Tool Information */}
            <div className="lg:col-span-2">
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                <h3 className="text-sm font-bold text-[#1e293b] mb-4 pb-2 border-b border-slate-100">Tool Information</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
                  <div>
                    <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Category</div>
                    <div className="text-sm font-medium text-[#1e293b] mt-1">
                      <span className="inline-flex px-2 py-0.5 rounded-md text-[11px] font-semibold border bg-slate-50 text-slate-600 border-slate-200">
                        {CATEGORY_LABELS[tool.category] || tool.category}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Type</div>
                    <div className="text-sm font-bold text-[#1e293b] mt-1">{tool.type}</div>
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Machine Assigned</div>
                    <div className="text-sm font-bold text-[#1e293b] mt-1">{tool.assignedMachineName ?? '—'}</div>
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Location</div>
                    <div className="text-sm font-bold text-[#1e293b] mt-1">{tool.location}</div>
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Purchase Date</div>
                    <div className="text-sm font-bold text-[#1e293b] mt-1">
                      {tool.purchaseDate ? new Date(tool.purchaseDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Purchase Cost</div>
                    <div className="text-sm font-bold text-indigo-700 mt-1">{formatCurrency(tool.purchaseCost || 0)}</div>
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Alert Threshold</div>
                    <div className="text-sm font-bold text-[#1e293b] mt-1">{tool.alertThresholdPercent}%</div>
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Last Maintenance</div>
                    <div className="text-sm font-bold text-[#1e293b] mt-1">
                      {tool.lastMaintenanceDate ? new Date(tool.lastMaintenanceDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Next Service Due</div>
                    <div className="text-sm font-bold text-[#1e293b] mt-1">
                      {tool.nextMaintenanceDue > 0 ? `In ${tool.nextMaintenanceDue.toLocaleString()} shots` : '—'}
                    </div>
                  </div>
                </div>
                {tool.notes && (
                  <div className="mt-5 pt-4 border-t border-slate-100">
                    <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Notes</div>
                    <p className="text-sm text-slate-600 font-medium">{tool.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Usage Stats */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6 shadow-sm">
            <h3 className="text-sm font-bold text-[#1e293b] mb-4">Usage Statistics</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5">
                <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Total Shots</div>
                <div className="text-lg font-bold text-[#1e293b] mt-1">
                  {tool.currentShotCount.toLocaleString()} / {tool.maxShotCount > 0 ? tool.maxShotCount.toLocaleString() : '—'}
                </div>
              </div>
              {tool.maxLifeHours && tool.maxLifeHours > 0 && (
                <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5">
                  <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Life Hours</div>
                  <div className="text-lg font-bold text-[#1e293b] mt-1">
                    {tool.currentLifeHours.toLocaleString()} / {tool.maxLifeHours.toLocaleString()}
                  </div>
                </div>
              )}
              <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5">
                <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Estimated Runs Left</div>
                <div className="text-lg font-bold text-[#1e293b] mt-1">
                  ~{tool.remainingShots > 0 && tool.maxShotCount > 0 ? Math.floor(tool.remainingShots / (tool.maxShotCount / 20)) : '—'}
                </div>
              </div>
              <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5">
                <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Life Tracking</div>
                <div className="text-sm font-bold text-[#1e293b] mt-1">
                  {tool.maxLifeHours && tool.maxLifeHours > 0 ? 'Hour-based' : 'Shot-based'}
                </div>
              </div>
            </div>
          </div>

          {/* Repair History */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <h3 className="text-sm font-bold text-[#1e293b] mb-4">Repair History</h3>
            {repairs.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-slate-50 border border-slate-200 mb-3">
                  <i className="ri-tools-line text-xl text-slate-300" />
                </div>
                <p className="text-sm font-medium text-slate-400">No repair history recorded for this tool asset</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50/70 border-b border-slate-200">
                      <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Date</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Type</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Repair By</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Cost</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Shots Before</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {repairs.map((r) => (
                      <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600 font-medium">
                          {r.repairDate ? new Date(r.repairDate).toLocaleDateString('en-IN') : '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold border bg-slate-50 text-slate-600 border-slate-200">
                            {r.repairType === 'MAINTENANCE' ? 'Maintenance' : r.repairType === 'RECONDITIONING' ? 'Reconditioning' : r.repairType === 'REPLACEMENT_PART' ? 'Replacement' : 'Scrap'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-700 font-medium">{r.repairBy}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs font-bold text-[#1e293b]">{formatCurrency(r.repairCost || 0)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600 font-medium">{(r.shotsBeforeRepair || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[10px] font-bold border ${r.status === 'RETURNED' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                              r.status === 'IN_REPAIR' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                'bg-slate-50 text-slate-600 border-slate-200'
                            }`}>
                            {r.status === 'SENT' ? 'Sent' : r.status === 'IN_REPAIR' ? 'In Repair' : 'Returned'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Update Shot Count Modal */}
      {showShotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-xs" onClick={() => setShowShotModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 p-6 z-10 border border-slate-100">
            <h3 className="text-lg font-bold text-[#1e293b] mb-4">Update Shot Count</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Current Shots</label>
                <input type="text" value={tool.currentShotCount.toLocaleString()} readOnly className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50 text-slate-500 font-semibold" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Add Shots</label>
                <input type="number" min={1} value={shotQty} onChange={(e) => setShotQty(e.target.value)} className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Reason</label>
                <input type="text" value={shotReason} onChange={(e) => setShotReason(e.target.value)} placeholder="e.g. Manual from shift log" className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2.5 mt-6">
              <button onClick={() => setShowShotModal(false)} className="px-4 py-2 text-sm font-semibold text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50 cursor-pointer">Cancel</button>
              <button onClick={handleUpdateShots} className="px-4 py-2 text-sm font-semibold text-white bg-[#2563eb] rounded-xl hover:bg-blue-700 cursor-pointer shadow-sm">Update</button>
            </div>
          </div>
        </div>
      )}

      {/* Send for Repair Modal */}
      {showRepairModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-xs" onClick={() => setShowRepairModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 z-10 my-8 border border-slate-100">
            <h3 className="text-lg font-bold text-[#1e293b] mb-4">Send {tool.toolNumber} for Repair</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Repair Type</label>
                <SearchableSelect
                  value={repairForm.repairType}
                  onChange={(val) => setRepairForm((p) => ({ ...p, repairType: val as MockToolRepair['repairType'] }))}
                  options={[
                    { value: 'MAINTENANCE', label: 'Maintenance' },
                    { value: 'RECONDITIONING', label: 'Reconditioning' },
                    { value: 'REPLACEMENT_PART', label: 'Replacement Part' },
                    { value: 'SCRAP', label: 'Scrap' },
                  ]}
                  placeholder="Select Repair Type"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Repair By <span className="text-rose-500">*</span></label>
                <input type="text" value={repairForm.repairBy} onChange={(e) => setRepairForm((p) => ({ ...p, repairBy: e.target.value }))} placeholder="Vendor name or In-House" className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Estimated Cost (₹)</label>
                <input type="number" value={repairForm.repairCost} onChange={(e) => setRepairForm((p) => ({ ...p, repairCost: e.target.value }))} className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Expected Return</label>
                <input type="date" value={repairForm.expectedReturn} onChange={(e) => setRepairForm((p) => ({ ...p, expectedReturn: e.target.value }))} className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Description</label>
                <textarea value={repairForm.description} onChange={(e) => setRepairForm((p) => ({ ...p, description: e.target.value }))} rows={2} maxLength={500} className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 resize-none font-medium" />
              </div>
              <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5">
                <div className="text-xs text-slate-500 font-medium">Shots at time of repair: <span className="font-bold text-[#1e293b]">{tool.currentShotCount.toLocaleString()}</span></div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2.5 mt-6">
              <button onClick={() => setShowRepairModal(false)} className="px-4 py-2 text-sm font-semibold text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50 cursor-pointer">Cancel</button>
              <button onClick={handleSendRepair} className="px-4 py-2 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-xl cursor-pointer shadow-sm">Send for Repair</button>
            </div>
          </div>
        </div>
      )}

      {/* Return from Repair Modal */}
      {showReturnModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-xs" onClick={() => setShowReturnModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 z-10 my-8 border border-slate-100">
            <h3 className="text-lg font-bold text-[#1e293b] mb-4">Return {tool.toolNumber} from Repair</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Return Date</label>
                <input type="date" value={returnForm.returnDate} onChange={(e) => setReturnForm((p) => ({ ...p, returnDate: e.target.value }))} className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Actual Repair Cost (₹)</label>
                <input type="number" value={returnForm.actualCost} onChange={(e) => setReturnForm((p) => ({ ...p, actualCost: e.target.value }))} className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Post-Repair Condition</label>
                <SearchableSelect
                  value={returnForm.condition}
                  onChange={(val) => setReturnForm((p) => ({ ...p, condition: val as 'Good' | 'Fair' | 'Poor' }))}
                  options={[
                    { value: 'Good', label: 'Good' },
                    { value: 'Fair', label: 'Fair' },
                    { value: 'Poor', label: 'Poor' },
                  ]}
                  placeholder="Select Post-Repair Condition"
                />
              </div>
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={returnForm.resetShots} onChange={(e) => setReturnForm((p) => ({ ...p, resetShots: e.target.checked }))} className="w-4 h-4 rounded border-slate-300 text-[#2563eb] focus:ring-blue-200" />
                  <span className="text-sm font-semibold text-[#1e293b]">Reset Shot Count to 0</span>
                </label>
                <p className="text-[11px] text-slate-400 mt-1 ml-6 font-medium">Enable if tool was fully reconditioned</p>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Notes</label>
                <textarea value={returnForm.notes} onChange={(e) => setReturnForm((p) => ({ ...p, notes: e.target.value }))} rows={2} maxLength={500} className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 resize-none font-medium" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2.5 mt-6">
              <button onClick={() => setShowReturnModal(false)} className="px-4 py-2 text-sm font-semibold text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50 cursor-pointer">Cancel</button>
              <button onClick={handleReturnRepair} className="px-4 py-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl cursor-pointer shadow-sm">Return from Repair</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}